import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Home,
  ArrowBack,
  Help,
  ContactSupport,
  Language,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const NotFound = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 2, delay: i * 0.1, repeat: Infinity, repeatType: 'reverse' }}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: theme.palette.primary.main,
              filter: 'blur(50px)',
            }}
          />
        ))}
      </Box>

      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Stack spacing={4} alignItems="center" textAlign="center">
            {/* 404 Number */}
            <Box sx={{ position: 'relative' }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '6rem', md: '10rem' },
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                404
              </Typography>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Language sx={{ fontSize: 100, opacity: 0.1, color: theme.palette.primary.main }} />
              </motion.div>
            </Box>

            {/* Message */}
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Page Not Found
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 500 }}>
              Oops! The page you're looking for doesn't exist or has been moved.
            </Typography>

            {/* Actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                Back to Home
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContactSupport />}
                onClick={() => navigate('/contact')}
              >
                Contact Support
              </Button>
            </Stack>

            {/* Help Section */}
            <Box sx={{ mt: 6, pt: 6, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Need help finding something?
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <IconButton onClick={() => navigate('/faqs')} sx={{ color: theme.palette.primary.main }}>
                  <Help />
                </IconButton>
                <IconButton onClick={() => navigate('/contact')} sx={{ color: theme.palette.primary.main }}>
                  <ContactSupport />
                </IconButton>
              </Stack>
            </Box>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NotFound;